import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-hot-toast";
import { 
  FiEdit2, 
  FiTrash2, 
  FiPlus, 
  FiLoader, 
  FiSearch,
  FiFilter,
  FiChevronLeft,
  FiChevronRight,
  FiPackage,
  FiDollarSign,
  FiCalendar,
  FiStar,
  FiCheckCircle,
  FiXCircle,
  FiEye
} from "react-icons/fi";
import { RiCoinsLine } from "react-icons/ri";

const PlanList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [filteredPlans, setFilteredPlans] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  // Edit Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [editFeatures, setEditFeatures] = useState([]);
  const [updating, setUpdating] = useState(false);
  
  // Delete Modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingPlan, setDeletingPlan] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // View Modal
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingPlan, setViewingPlan] = useState(null);

  const API_URL = "https://api.dreamtoday.in/api/admin";

  // Fetch all plans
  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/getallplans`, {
        withCredentials: true
      });
      
      console.log("Plans response:", response.data); // Debug log
      
      if (response.data.success) {
        setPlans(response.data.plans);
        setFilteredPlans(response.data.plans);
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
      toast.error("Failed to fetch plans");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  // Search and filter
  useEffect(() => {
    let filtered = plans;
    
    // Apply search
    if (searchTerm) {
      filtered = filtered.filter(plan => 
        plan.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        plan.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(plan => plan.status === statusFilter);
    }
    
    setFilteredPlans(filtered);
    setCurrentPage(1);
  }, [searchTerm, statusFilter, plans]);

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredPlans.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredPlans.length / itemsPerPage);

  // Handle Edit
  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setEditFeatures(plan.features || []);
    setShowEditModal(true);
  };

  // Handle Update
  const handleUpdate = async (e) => {
    e.preventDefault();
    setUpdating(true);

    try {
      const updatedData = {
        title: editingPlan.title,
        description: editingPlan.description,
        coins: Number(editingPlan.coins),
        bonusCoins: Number(editingPlan.bonusCoins),
        price: Number(editingPlan.price),
        features: editFeatures.filter(f => f.trim() !== ""),
        validity: Number(editingPlan.validity),
        status: editingPlan.status
      };

      const response = await axios.put(
        `${API_URL}/updateplan/${editingPlan._id}`,
        updatedData,
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success("Plan updated successfully");
        setShowEditModal(false);
        fetchPlans();
      }
    } catch (error) {
      console.error("Error updating plan:", error);
      toast.error(error.response?.data?.message || "Failed to update plan");
    } finally {
      setUpdating(false);
    }
  };

  // Handle Delete
  const handleDelete = async () => {
    if (!deletingPlan) return;
    
    setDeleting(true);
    try {
      const response = await axios.delete(
        `${API_URL}/deleteplan/${deletingPlan._id}`,
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success("Plan deleted successfully");
        setShowDeleteModal(false);
        fetchPlans();
      }
    } catch (error) {
      console.error("Error deleting plan:", error);
      toast.error(error.response?.data?.message || "Failed to delete plan");
    } finally {
      setDeleting(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { bg: "bg-green-100", text: "text-green-800", icon: FiCheckCircle },
      inactive: { bg: "bg-red-100", text: "text-red-800", icon: FiXCircle }
    };
    
    const config = statusConfig[status] || statusConfig.inactive;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon className="text-xs" />
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <FiLoader className="animate-spin text-4xl text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 md:mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 flex items-center gap-3">
              <FiPackage className="text-blue-500" />
              Plans Management
            </h1>
            <p className="text-gray-600 mt-2 text-sm md:text-base">
              Total {filteredPlans.length} plans found
            </p>
          </div>
          
          <button
            onClick={() => navigate("/plan/create")}
            className="w-full sm:w-auto px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all flex items-center justify-center gap-2"
          >
            <FiPlus />
            Create New Plan
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search plans by title or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="sm:w-48 relative">
              <FiFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>

        {/* Plans Grid */}
        {currentItems.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <FiPackage className="text-6xl text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Plans Found</h3>
            <p className="text-gray-500 mb-6">Get started by creating your first plan</p>
            <button
              onClick={() => navigate("/plan/create")}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all"
            >
              <FiPlus />
              Create New Plan
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {currentItems.map((plan) => (
                <div
                  key={plan._id}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                >
                  {/* Plan Header */}
                  <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-4 text-white">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-xl font-bold mb-1">{plan.title}</h3>
                        <p className="text-sm text-blue-100">{plan.description || "No description"}</p>
                      </div>
                      {getStatusBadge(plan.status)}
                    </div>
                  </div>

                  {/* Plan Details */}
                  <div className="p-4">
                    <div className="space-y-3 mb-4">
                      <div className="flex items-center gap-3 text-sm">
                        <RiCoinsLine className="text-yellow-500 text-lg" />
                        <span className="text-gray-600">Coins:</span>
                        <span className="font-semibold text-gray-800">{plan.coins}</span>
                        {plan.bonusCoins > 0 && (
                          <span className="text-green-600 text-xs">+{plan.bonusCoins} Bonus</span>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-3 text-sm">
                        <FiDollarSign className="text-green-500 text-lg" />
                        <span className="text-gray-600">Price:</span>
                        <span className="font-semibold text-gray-800">₹{plan.price}</span>
                      </div>
                      
                      <div className="flex items-center gap-3 text-sm">
                        <FiCalendar className="text-blue-500 text-lg" />
                        <span className="text-gray-600">Validity:</span>
                        <span className="font-semibold text-gray-800">
                          {plan.validity === 0 ? "Lifetime" : `${plan.validity} days`}
                        </span>
                      </div>
                    </div>

                    {/* Features */}
                    <div className="mb-4">
                      <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                        <FiStar className="text-yellow-500" />
                        Features:
                      </p>
                      <ul className="space-y-1">
                        {plan.features?.slice(0, 3).map((feature, idx) => (
                          <li key={idx} className="text-xs text-gray-600 flex items-start gap-2">
                            <span className="text-green-500 mt-1">•</span>
                            <span>{feature}</span>
                          </li>
                        ))}
                        {plan.features?.length > 3 && (
                          <li className="text-xs text-blue-500">
                            +{plan.features.length - 3} more features
                          </li>
                        )}
                      </ul>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <span className="text-xs text-gray-400">
                        Created: {formatDate(plan.createdAt)}
                      </span>
                      
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setViewingPlan(plan);
                            setShowViewModal(true);
                          }}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                          title="View Details"
                        >
                          <FiEye />
                        </button>
                        <button
                          onClick={() => handleEdit(plan)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-all"
                          title="Edit Plan"
                        >
                          <FiEdit2 />
                        </button>
                        <button
                          onClick={() => {
                            setDeletingPlan(plan);
                            setShowDeleteModal(true);
                          }}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all"
                          title="Delete Plan"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-8">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FiChevronLeft />
                </button>
                
                <span className="px-4 py-2 bg-blue-500 text-white rounded-lg">
                  Page {currentPage} of {totalPages}
                </span>
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FiChevronRight />
                </button>
              </div>
            )}
          </>
        )}

        {/* Edit Modal */}
        {showEditModal && editingPlan && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                  <FiEdit2 className="text-green-500" />
                  Edit Plan
                </h2>

                <form onSubmit={handleUpdate} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                    <input
                      type="text"
                      value={editingPlan.title}
                      onChange={(e) => setEditingPlan({...editingPlan, title: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <textarea
                      value={editingPlan.description || ""}
                      onChange={(e) => setEditingPlan({...editingPlan, description: e.target.value})}
                      rows="3"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Coins *</label>
                      <input
                        type="number"
                        value={editingPlan.coins}
                        onChange={(e) => setEditingPlan({...editingPlan, coins: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        required
                        min="1"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Bonus Coins</label>
                      <input
                        type="number"
                        value={editingPlan.bonusCoins}
                        onChange={(e) => setEditingPlan({...editingPlan, bonusCoins: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        min="0"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Price (₹) *</label>
                      <input
                        type="number"
                        value={editingPlan.price}
                        onChange={(e) => setEditingPlan({...editingPlan, price: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        required
                        min="1"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Validity (Days)</label>
                      <select
                        value={editingPlan.validity}
                        onChange={(e) => setEditingPlan({...editingPlan, validity: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="7">7 Days</option>
                        <option value="15">15 Days</option>
                        <option value="30">30 Days</option>
                        <option value="60">60 Days</option>
                        <option value="90">90 Days</option>
                        <option value="180">180 Days</option>
                        <option value="365">365 Days</option>
                        <option value="0">Lifetime</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <select
                      value={editingPlan.status}
                      onChange={(e) => setEditingPlan({...editingPlan, status: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Features</label>
                    {editFeatures.map((feature, idx) => (
                      <div key={idx} className="flex gap-2 mb-2">
                        <input
                          type="text"
                          value={feature}
                          onChange={(e) => {
                            const newFeatures = [...editFeatures];
                            newFeatures[idx] = e.target.value;
                            setEditFeatures(newFeatures);
                          }}
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder={`Feature ${idx + 1}`}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const newFeatures = editFeatures.filter((_, i) => i !== idx);
                            setEditFeatures(newFeatures);
                          }}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => setEditFeatures([...editFeatures, ""])}
                      className="mt-2 px-4 py-2 border border-dashed border-gray-300 rounded-lg w-full text-gray-600 hover:bg-gray-50"
                    >
                      + Add Feature
                    </button>
                  </div>

                  <div className="flex justify-end gap-3 mt-6">
                    <button
                      type="button"
                      onClick={() => setShowEditModal(false)}
                      className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={updating}
                      className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      {updating ? <FiLoader className="animate-spin" /> : <FiCheckCircle />}
                      {updating ? "Updating..." : "Update Plan"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && deletingPlan && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-md w-full p-6">
              <div className="text-center">
                <FiTrash2 className="text-5xl text-red-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">Delete Plan</h3>
                <p className="text-gray-600 mb-6">
                  Are you sure you want to delete "{deletingPlan.title}"? This action cannot be undone.
                </p>
                
                <div className="flex justify-center gap-3">
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={deleting}
                    className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 flex items-center gap-2"
                  >
                    {deleting ? <FiLoader className="animate-spin" /> : <FiTrash2 />}
                    {deleting ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* View Modal */}
        {showViewModal && viewingPlan && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-start mb-6">
                  <h2 className="text-2xl font-bold flex items-center gap-2">
                    <FiEye className="text-blue-500" />
                    Plan Details
                  </h2>
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                  >
                    <FiXCircle className="text-xl" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-4 rounded-lg">
                    <h3 className="text-xl font-bold">{viewingPlan.title}</h3>
                    <p className="text-blue-100 mt-1">{viewingPlan.description || "No description"}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500">Coins</p>
                      <p className="text-lg font-semibold">{viewingPlan.coins}</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500">Bonus Coins</p>
                      <p className="text-lg font-semibold">{viewingPlan.bonusCoins || 0}</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500">Price</p>
                      <p className="text-lg font-semibold">₹{viewingPlan.price}</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500">Validity</p>
                      <p className="text-lg font-semibold">
                        {viewingPlan.validity === 0 ? "Lifetime" : `${viewingPlan.validity} days`}
                      </p>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2 flex items-center gap-1">
                      <FiStar className="text-yellow-500" />
                      Features
                    </h4>
                    <ul className="space-y-2">
                      {viewingPlan.features?.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-gray-700">
                          <span className="text-green-500 mt-1">•</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Status</p>
                      {getStatusBadge(viewingPlan.status)}
                    </div>
                    <div>
                      <p className="text-gray-500">Created At</p>
                      <p>{formatDate(viewingPlan.createdAt)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanList;